import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import type { PageProps } from 'gatsby';
import { navigate } from 'gatsby';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';

import {
  BottomControlsBar,
  BottomControlsInner,
  PdfWrapper,
  ScrollForms,
  TwoColumnLayout,
} from '@components/DiscernSummary/DiscernCuration.style';
import type {
  DynamicResourceFormsHandle,
  FormsMeta,
} from '@components/DynamicResourceForms/DynamicResourceForms';
import DynamicResourceForms from '@components/DynamicResourceForms/DynamicResourceForms';
import Icon from '@components/Icon/Icon';
import IconButton from '@components/IconButton';
import PdfCurationViewer from '@components/PdfCuration/PdfCurationViewer';
import type { FHIRResource } from '@components/PdfCuration/types';

import { DISCERN_JOBS } from '@constants/routes';

import { AutoCurationProvider } from '@contexts/auto-curation';

import { useAutoCuration } from '@hooks/useAutoCuration';
import { useSnackbar } from '@hooks/useSnackbar';

import type { FormType } from '@utils/fhir-mappers';
import { mapBBoxTitleToFormType } from '@utils/mapBBoxTitleToFormType';
import { findLocationResource } from '@utils/ocr/locationFacility';
import { sortDiscernResults } from '@utils/sortDiscernResults';

import MainWrapper from '@views/Records/Common/MainWrapper';

import {
  useGetDiscernDocumentQuery,
  useGetDiscernResultQuery,
  useUpdateDiscernResultStatusMutation,
} from 'generated/discern.generated';
import type { DiscernResultModel } from 'generated/types';
import { DiscernResultStatus } from 'generated/types';

import type { BBoxResource } from '../../../../../types/bbox';

/* -------------------------------------------------------------------------- */

const messages = defineMessages({
  title: {
    id: 'discernCuration.title',
    defaultMessage: 'Curation Review',
  },
  formsLoading: {
    id: 'discernCuration.formsLoading',
    defaultMessage: 'Forms loading...',
  },
  addManualResource: {
    id: 'discernCuration.addManualResource',
    defaultMessage: 'Add new manual resource',
  },
  noResourcesDetected: {
    id: 'discernCuration.noResourcesDetected',
    defaultMessage: 'No resources detected in BBOX.',
  },
  noResources: {
    id: 'discernCuration.noResources',
    defaultMessage: 'No resources',
  },
  autoUpdateWhenReady: {
    id: 'discernCuration.autoUpdateWhenReady',
    defaultMessage:
      'This may take a few minutes. The page will automatically update when results are ready.',
  },
  back: {
    id: 'discernCuration.back',
    defaultMessage: 'Back to Patient Table',
  },
  waitForProcess: {
    id: 'discernCuration.waitForProcess',
    defaultMessage: 'Please wait while the AI model processes your document...',
  },
  structuring: {
    id: 'discernCuration.structuring',
    defaultMessage: 'Model is Structuring',
  },
  successMessage: {
    id: 'successMessage.updateDiscernResultStatus',
    defaultMessage: 'Successfully updated selected resource',
  },
  errorMessage: {
    id: 'errorMessage.updateDiscernResultStatus',
    defaultMessage: 'Failed to update selected resource',
  },
});

function Content({ documentId }: { documentId: string }) {
  const { formatMessage } = useIntl();
  const { palette } = useTheme();

  // Get initial resource index from query parameter
  const discern_result_id = useSearchParam('result_id');

  const [bboxData, setBBoxData] = useState<FHIRResource[] | null>(null);
  const [filteredBBoxData, setFilteredBBoxData] = useState<
    FHIRResource[] | null
  >(null);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [totalResults, setTotalResults] = useState<DiscernResultModel[]>([]);

  // Fetch document to check status
  const { data: documentData, loading: documentLoading } =
    useGetDiscernDocumentQuery({
      variables: {
        input: { discern_document_id: documentId },
      },
    });
  const { data, loading: resultLoading } = useGetDiscernResultQuery({
    variables: {
      input: {
        discern_result_id: discern_result_id ?? '',
      },
    },
    skip: !discern_result_id,
    fetchPolicy: 'cache-and-network', // fetch a fresh pdf URL in background to avoid expiry
  });

  const loading = documentLoading || resultLoading;
  const {
    removeManualResource,
    state: { manualResources },
  } = useAutoCuration();

  // Mutation to update Discern result status
  const [updateResultStatus] = useUpdateDiscernResultStatusMutation();

  // Check if we're waiting for results (status is Structuring and no results yet)
  const documentStatus = documentData?.getDiscernDocument.status;
  const results = documentData?.getDiscernDocument.job?.results ?? [];
  const sortedResults = useMemo(() => sortDiscernResults(results), [results]);

  const [totalResources, setTotalResources] = React.useState(0);

  useEffect(() => {
    const totalResults = sortedResults.filter(
      (r) =>
        mapBBoxTitleToFormType(r.resource_name || 'null') !== null &&
        r.status !== 'Complete' &&
        r.status !== 'Delete'
    );
    setTotalResults(totalResults as DiscernResultModel[]);
    const totalResource = totalResults.length;

    const currentIndex = totalResults.findIndex(
      (r) => r.discern_result_id === discern_result_id
    );
    setCurrentIndex(currentIndex);

    const newTotal = totalResource + manualResources.length;
    setTotalResources(newTotal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, loading, manualResources, discern_result_id, sortedResults]);
  const result = data?.getDiscernResult;
  // TODO set active index?
  const hasResults =
    result && result.details?.bounding_boxes && result.details.ocr_data;
  const isStructuring = documentStatus === 'Structuring' && !hasResults;

  const vitallPatientId =
    documentData?.getDiscernDocument.vitall_patient?.vitall_patient_id;
  const patientId = documentData?.getDiscernDocument.vitall_patient?.patient_id;

  /**
   * The result payload holds every resource extracted from this sub-job, i.e.
   * this whole page group — one `results.json` per sub-job. Only the resource at
   * `bbox_id` is curated here, but the group's `Location` resource is the sole
   * source of the facility for all of them, so keep hold of it.
   */
  const groupLocation = useMemo(
    () =>
      findLocationResource(
        result?.details?.bounding_boxes as FHIRResource[] | undefined
      ),
    [result?.details?.bounding_boxes]
  );

  useEffect(() => {
    const AllBBoxes = result?.details?.bounding_boxes as
      FHIRResource[] | undefined;
    const filtered =
      result?.bbox_id !== undefined && AllBBoxes
        ? [AllBBoxes[Number(result.bbox_id)]]
        : AllBBoxes;
    if (filtered && Array.isArray(filtered)) {
      setBBoxData(filtered as FHIRResource[]);
      setFilteredBBoxData(filtered as FHIRResource[]);
    } else if (!loading) {
      // No results yet; clear any existing data
      setBBoxData(null);
      setFilteredBBoxData(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, loading, result?.discern_result_id]);

  const formsRef = useRef<DynamicResourceFormsHandle | null>(null);

  /* --------------------------------------------------------------------------
     Meta from child
  -------------------------------------------------------------------------- */

  const [formsLoaded, setFormsLoaded] = useState<boolean>(false);
  const [availableFormTypes, setAvailableFormTypes] = useState<FormType[]>([]);

  const handleMetaChange = useCallback(
    (meta: FormsMeta) => {
      setFormsLoaded(meta.formsLoaded);

      if (meta.activeIndex !== activeIndex) {
        setActiveIndex(meta.activeIndex);
      }
    },
    [activeIndex]
  );

  const handleFormsMapChange = useCallback((ft: FormType[]) => {
    setAvailableFormTypes(ft);
  }, []);

  const canGoPrev = totalResources > 0 && activeIndex > 0;
  const canGoNext = totalResources > 0 && activeIndex < totalResources - 1;

  /* Add form menu state */
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(menuAnchor);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>) =>
    setMenuAnchor(e.currentTarget);
  const closeMenu = () => setMenuAnchor(null);

  const handleAddForm = (formType: FormType) => {
    formsRef.current?.addManualForm(formType);
    closeMenu();
  };
  const count = manualResources.filter((r) => {
    const match = r.id.match(/^manual-(\d+)$/);
    return match && Number(match[1]) < Number(currentIndex);
  }).length;
  useEffect(() => {
    setActiveIndex((prev) =>
      prev === currentIndex + count ? prev : currentIndex + count
    );
  }, [currentIndex, count]);
  const { enqueueSnackbar } = useSnackbar();
  const goPrevious = () => {
    manualResources.forEach((r) => {
      removeManualResource(r.id);
    });
    if (totalResults[currentIndex - 1]?.discern_result_id) {
      navigate(
        `${window.location.pathname}?result_id=${totalResults[currentIndex - 1].discern_result_id}`,
        { replace: true }
      );
    }
  };
  const goNext = () => {
    manualResources.forEach((r) => {
      removeManualResource(r.id);
    });
    if (totalResults[currentIndex + 1]?.discern_result_id) {
      navigate(
        `${window.location.pathname}?result_id=${totalResults[currentIndex + 1].discern_result_id}`,
        { replace: true }
      );
    }
  };

  // Handle resource saved - update Discern result status to Complete
  const handleResourceSaved = useCallback(
    async (
      discern_result_id: string,
      resource?: BBoxResource | null,
      nextId?: string | null
    ) => {
      try {
        // Get the job results to find matching result
        const jobResults = documentData?.getDiscernDocument.job?.results;

        if (!jobResults || !Array.isArray(jobResults)) {
          // eslint-disable-next-line no-console
          console.warn('No job results found to update Discern result status', {
            resourceTitle: resource?.Resource_title,
          });
          return;
        }
        // Find the matching Discern result by bbox_id
        // Complete all the metadata e.g Location
        const metaDataResults = jobResults.filter(
          (r) =>
            (!mapBBoxTitleToFormType(r.resource_name || '') &&
              r.status !== DiscernResultStatus.Complete) ||
            r.discern_result_id === discern_result_id
        );

        if (metaDataResults.length > 0) {
          await Promise.all(
            metaDataResults.map((result) =>
              updateResultStatus({
                variables: {
                  input: {
                    discern_result_id: result.discern_result_id,
                    status: DiscernResultStatus.Complete,
                  },
                },
              })
            )
          );
          enqueueSnackbar({
            message: formatMessage(messages.successMessage),
            variant: 'success',
          });
          if (nextId === discern_result_id) {
            const NewResults = sortedResults.filter(
              (r) =>
                mapBBoxTitleToFormType(r.resource_name || 'null') !== null &&
                r.discern_result_id !== discern_result_id &&
                r.discern_result_id !== nextId
            );
            if (NewResults.length > 0) {
              navigate(
                `${window.location.pathname}?result_id=${NewResults[0].discern_result_id}`,
                {
                  replace: true,
                }
              );
            } else {
              navigate(DISCERN_JOBS);
            }
          } else {
            navigate(`${window.location.pathname}?result_id=${nextId}`, {
              replace: true,
            });
          }
        } else {
          // eslint-disable-next-line no-console
          console.warn('No matching Discern result found for resource', {
            resourceTitle: resource?.Resource_title,
            availableResults: jobResults.map((r: any) => r.resource_name),
          });
        }
      } catch (error) {
        enqueueSnackbar({
          message: formatMessage(messages.errorMessage),
          variant: 'error',
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      documentData?.getDiscernDocument.job?.results,
      sortedResults,
      updateResultStatus,
    ]
  );

  const hasResources = !!bboxData && bboxData.length > 0;
  return (
    <>
      {/* Loading overlay when model is structuring */}
      <Backdrop
        open={isStructuring || loading}
        sx={{
          color: palette.common.white,
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
          }}
        >
          {vitallPatientId && (
            <Button
              startIcon={<Icon name="arrow-left" type="fas" />}
              onClick={() => {
                navigate(DISCERN_JOBS);
              }}
              color="primary"
              variant="outlined"
            >
              {formatMessage(messages.back)}
            </Button>
          )}
        </Box>
        <CircularProgress color="inherit" size={60} />
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
            {formatMessage(messages.structuring)}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {formatMessage(messages.waitForProcess)}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.7 }}>
            {formatMessage(messages.autoUpdateWhenReady)}
          </Typography>
        </Box>
      </Backdrop>
      {hasResources && !loading ? (
        <TwoColumnLayout>
          {/* LEFT — PDF */}
          <PdfWrapper className="pdf-panel shadow-sm">
            <PdfCurationViewer
              embedded
              pdfUrl={data?.getDiscernResult.pdf_url?.url}
              bboxData={filteredBBoxData || bboxData}
              setBboxData={setBBoxData}
              ocrOverride={result?.details?.ocr_data || undefined}
              activeIndex={activeIndex}
            />
          </PdfWrapper>

          {/* RIGHT — FORM + STICKY BAR */}
          <div className="form-panel shadow-sm">
            <ScrollForms>
              <DynamicResourceForms
                ref={formsRef}
                pdfUrl={data?.getDiscernResult.pdf_url?.url}
                bboxData={bboxData}
                locationResource={groupLocation}
                discern_result_id={discern_result_id || ''}
                patientId={patientId}
                activeIndex={activeIndex}
                setActiveIndex={setActiveIndex}
                totalResources={totalResources}
                nextId={
                  totalResults[currentIndex + 1]?.discern_result_id ??
                  totalResults[0]?.discern_result_id
                }
                onMetaChange={handleMetaChange}
                bbox_id={result?.bbox_id || ''}
                onFormsMapChange={handleFormsMapChange}
                curationMode
                onResourceSaved={handleResourceSaved}
              />
            </ScrollForms>

            {/* Sticky aligned bar */}
            <BottomControlsBar>
              <BottomControlsInner>
                <IconButton
                  name="chevron-left"
                  iconType="fas"
                  onClick={goPrevious}
                  disabled={!canGoPrev}
                  sx={{
                    backgroundColor: canGoPrev
                      ? palette.primary.main
                      : palette.action.disabledBackground,
                    color: palette.primary.contrastText,
                    borderRadius: '12px',
                    width: 48,
                    height: 48,
                  }}
                />

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                  }}
                >
                  <Tooltip
                    title={formatMessage(
                      formsLoaded
                        ? messages.addManualResource
                        : messages.formsLoading
                    )}
                    arrow
                  >
                    <span>
                      <Button
                        variant="outlined"
                        disabled={!formsLoaded}
                        onClick={openMenu}
                        sx={{
                          borderRadius: '999px',
                          textTransform: 'none',
                          fontWeight: 500,
                          px: 2.5,
                          py: 1,
                        }}
                      >
                        + Add form
                      </Button>
                    </span>
                  </Tooltip>

                  <span className="text-sm text-gray-500">
                    {totalResources > 0
                      ? `Resource ${activeIndex + 1} of ${totalResources}`
                      : formatMessage(messages.noResources)}
                  </span>

                  <Menu
                    anchorEl={menuAnchor}
                    open={menuOpen}
                    onClose={closeMenu}
                  >
                    {availableFormTypes.map((type) => (
                      <MenuItem
                        key={type}
                        onClick={() => handleAddForm(type)}
                        sx={{ fontSize: 14 }}
                      >
                        {type}
                      </MenuItem>
                    ))}
                  </Menu>
                </div>

                <IconButton
                  name="chevron-right"
                  iconType="fas"
                  onClick={goNext}
                  disabled={!canGoNext}
                  sx={{
                    backgroundColor: canGoNext
                      ? palette.primary.main
                      : palette.action.disabledBackground,
                    color: palette.primary.contrastText,
                    borderRadius: '12px',
                    width: 48,
                    height: 48,
                  }}
                />
              </BottomControlsInner>
            </BottomControlsBar>
          </div>
        </TwoColumnLayout>
      ) : (
        <p>{!loading ? formatMessage(messages.noResourcesDetected) : ''}</p>
      )}
    </>
  );
}

export default function DiscernCurationPage({ params }: PageProps) {
  const { formatMessage } = useIntl();
  const documentId = params.documentId as string;

  return (
    <MainWrapper
      backgroundIcon="discern"
      friendlyName={formatMessage(messages.title)}
      loading={false}
      title={formatMessage(messages.title)}
      maxContentWidth={false}
      style={{ padding: '0 24px' }}
      hideTitle
    >
      <AutoCurationProvider>
        <Content documentId={documentId} />
      </AutoCurationProvider>
    </MainWrapper>
  );
}
